import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import usePartners from '../hooks/partners/usePartners.ts';
import { Partner } from '../models/partner.ts';
import {
  Box,
  Title,
  Button,
  Group,
  Stack,
  Paper,
  TextInput,
  Anchor,
  Text,
} from '@mantine/core';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { useDisclosure } from '@mantine/hooks';
import useCreatePartner from '../hooks/partners/useCreatePartner.ts';
import { SupportStoreTypeBadges } from '../components/ui/SupportStoreTypeBadges.tsx';
import { notifySuccess } from '../utils/NotifySuccess.tsx';
import CreatePartnerModal from '../components/modal/partner/CreatePartnerModal.tsx';

const PAGE_SIZE = 10;

export default function PartnerListPage() {
  const [page, setPage] = useState(1);
  const [searchedName, setSearchedName] = useState('');
  const queryClient = useQueryClient();
  const { mutate: createPartner, isPending: isCreatePartnerPending } =
    useCreatePartner();

  const [
    createPartnerModalOpened,
    { open: openCreatePartnerModal, close: closeCreatePartnerModal },
  ] = useDisclosure(false);

  function handleSearchChange(searchValue: string) {
    setSearchedName(searchValue);
    setPage(1);
  }

  const { data = [], isLoading } = usePartners();
  const filteredPartners = useMemo(() => {
    if (searchedName === '') return data;

    const lowerCaseSearchedName = searchedName.toLowerCase();
    return data.filter((partner) => {
      return (
        partner.name.toLowerCase().includes(lowerCaseSearchedName) ||
        partner.displayNameForHuman
          ?.toLowerCase()
          .includes(lowerCaseSearchedName)
      );
    });
  }, [data, searchedName]);
  const records = filteredPartners.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const columns: DataTableColumn<Partner>[] = [
    {
      title: '業者名稱',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'name',
      width: '30%',
      render: ({ id, name }: { id: string; name: string }) => (
        <Anchor href={`/partners/${id}`}>
          <Text size="sm">{name}</Text>
        </Anchor>
      ),
    },
    {
      title: '前台顯示名稱',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'displayNameForHuman',
      width: '40%',
      render: ({ displayNameForHuman }) => displayNameForHuman || '-',
    },
    {
      title: '可管理場站類型',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'supportStoreTypes',
      width: '20%',
      render: (record) => (
        <SupportStoreTypeBadges supportStoreTypes={record.supportStoreTypes} />
      ),
    },
  ];

  function handleConfirmCreatePartner({ formValues }) {
    if (isCreatePartnerPending) return;

    createPartner(
      { ...formValues },
      {
        onSuccess: () => {
          closeCreatePartnerModal();
          notifySuccess('新增業者成功');
          queryClient.invalidateQueries({
            queryKey: ['partners'],
          });
        },
      },
    );
  }

  return (
    <>
      {createPartnerModalOpened && (
        <CreatePartnerModal
          opened={createPartnerModalOpened}
          onClose={closeCreatePartnerModal}
          onConfirm={handleConfirmCreatePartner}
          isPending={isCreatePartnerPending}
        />
      )}

      <Paper radius="lg" p="xl" shadow="xl">
        <Stack gap="xs">
          <Group justify="space-between">
            <Title order={2}>業者管理</Title>
            <Button
              leftSection={<IconPlus size={20} />}
              onClick={openCreatePartnerModal}
            >
              新增業者
            </Button>
          </Group>
          <Group style={{ height: '60px' }}>
            <TextInput
              style={{ width: '300px' }}
              placeholder="搜尋業者名稱或前台顯示名稱"
              leftSection={<IconSearch size={16} />}
              value={searchedName}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </Group>
          <Box>
            <DataTable
              idAccessor="id"
              highlightOnHover
              height={560}
              columns={columns}
              records={records}
              recordsPerPage={PAGE_SIZE}
              page={page}
              onPageChange={setPage}
              fetching={isLoading}
              totalRecords={filteredPartners.length}
              verticalSpacing="sm"
              noRecordsText="查無業者"
            />
          </Box>
        </Stack>
      </Paper>
    </>
  );
}
